import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../api';

const AuthContext = createContext(null);

// Role hierarchy for permission checks
const ROLE_HIERARCHY = {
  super_admin: 100,
  admin: 90,
  account_manager: 70,
  operator: 70,
  support_operator: 50,
  customer: 20,
  carrier: 20,
};

// Permission definitions per role
const PERMISSIONS = {
  super_admin: ['all'],
  admin: ['dashboard', 'loads', 'customers', 'carriers', 'facilities', 'reports', 'reload', 'settings', 'commission_all'],
  account_manager: ['dashboard', 'loads', 'customers', 'carriers', 'facilities', 'reports', 'reload', 'commission_own'],
  operator: ['dashboard', 'loads', 'customers', 'carriers', 'facilities', 'reports', 'reload', 'commission_own'],
  support_operator: ['dashboard', 'loads', 'customers', 'carriers', 'facilities', 'reload'],
  customer: ['loads_own'],
  carrier: ['loads_assigned', 'reload_restricted'],
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Refresh user data from the backend to get latest role/permissions
  const refreshUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      
      const response = await authAPI.getMe();
      const userData = response.data;
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (error) {
      // If refresh fails (e.g., token expired), clear auth state
      console.error('Failed to refresh user data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      if (token && savedUser) {
        // First, set from localStorage for immediate UI
        setUser(JSON.parse(savedUser));
        // Then refresh from backend to get latest role
        await refreshUserData();
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await authAPI.register({ email, password, name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    const role = user.role || 'operator';
    const perms = PERMISSIONS[role] || [];
    return perms.includes('all') || perms.includes(permission);
  };

  // Check if user can access a module
  const canAccess = (module) => {
    if (!user) return false;
    const role = user.role || 'operator';
    const perms = PERMISSIONS[role] || [];
    
    if (perms.includes('all')) return true;
    
    // Check for exact match or related permissions
    return perms.some(p => p === module || p.startsWith(module));
  };

  // Check if user is admin or super_admin
  const isAdmin = () => {
    if (!user) return false;
    return ['super_admin', 'admin'].includes(user.role);
  };

  // Check if user is super_admin
  const isSuperAdmin = () => {
    if (!user) return false;
    return user.role === 'super_admin';
  };

  // Check if user is a carrier
  const isCarrier = () => {
    if (!user) return false;
    return user.role === 'carrier';
  };

  // Check if user is a customer
  const isCustomer = () => {
    if (!user) return false;
    return user.role === 'customer';
  };

  // Check if user can access commission data
  const canAccessCommission = () => {
    if (!user) return false;
    
    // Get the user's actual module permissions
    const modulePerms = user.module_permissions;
    
    // If module_permissions exists (even empty array), use it
    if (modulePerms !== undefined && modulePerms !== null) {
      return modulePerms.includes('commission');
    }
    
    // If no explicit permissions set (undefined), fall back to role defaults
    // This only happens for users created before the permission system
    const role = user.role || 'operator';
    const defaultPerms = {
      'super_admin': true,
      'admin': true,
      'account_manager': true,
      'operator': false,
      'support_operator': false,
      'customer': false,
      'carrier': false,
    };
    return defaultPerms[role] || false;
  };

  // Get user's role level
  const getRoleLevel = () => {
    if (!user) return 0;
    return ROLE_HIERARCHY[user.role] || 0;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout,
      refreshUserData,
      hasPermission,
      canAccess,
      isAdmin,
      isSuperAdmin,
      isCarrier,
      isCustomer,
      canAccessCommission,
      getRoleLevel
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};